import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';

import { debounceTime, firstValueFrom, Subscription, filter, take } from 'rxjs';

import { RippleModule } from 'primeng/ripple';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';

import { ViewScopeService, Customer, Company } from '../../../services/view-scope.service';
import { TranslateService } from '../../../services/translate.service';
import { LayoutService } from '../../../layout/service/layout.service';

interface CustomerActivity {
    id: number;
    userName: string;
    pendingFiles: number;
    pendingRows: number;
}

@Component({
    standalone: true,
    selector: 'app-customer-activity-widget', 
    imports: [CommonModule, TableModule, ButtonModule, RippleModule, ChartModule],
    templateUrl: './customersactivitywidget.html',
    styleUrls: ['./customersactivitywidget.scss']
    // Remove 'providers: [ProductService]' as it's no longer needed
})
export class CustomersActivityWidget implements OnInit, OnDestroy {
    customerActivities: CustomerActivity[] = [];
    allActivities: CustomerActivity[] = [];
    showPendingOnly: boolean = false;
    currentCustomerId: number | null = null;
    currentCustomerActivity: CustomerActivity | null = null;
    totalPendingFiles: number = 0;
    totalPendingRows: number = 0;
    currentPendingFiles: number = 0;
    currentPendingRows: number = 0;
    otherPendingFiles: number = 0;
    otherPendingRows: number = 0;
    currentFilesShare: number = 0;
    currentRowsShare: number = 0;
    workComparisonChartData: any;
    workComparisonChartOptions: any;
    subscription: Subscription;

    constructor(private viewScopeService: ViewScopeService,
                private layoutService: LayoutService,
                public translateServ: TranslateService
    ) {
        this.subscription = this.layoutService.configUpdate$.pipe(debounceTime(25)).subscribe(() => {
            this.setWorkComparisonChart();
        });
    }

    async ngOnInit() {
        // Wait for the company selection to resolve first - getCustomers()/getPendingWorkForCustomer()
        // both need it (via getSelectedCompanyId()) and would otherwise race SelectAccountComponent,
        // which resolves/saves it asynchronously on the same tick this widget mounts.
        await firstValueFrom(this.viewScopeService.selectedCompany$.pipe(
            filter((company): company is Company => company != null),
            take(1)
        ));

        // Fetch customers and their file counts
        const customers: Customer[] = await firstValueFrom(this.viewScopeService.getCustomers());
        const pendingMap: { [customerId: number]: { files: number, rows: number } } = {};
        await firstValueFrom(this.viewScopeService.getPendingWorkForCustomer(false)).then((data: any[]) => {
            data.forEach(item => {
                pendingMap[item.CustomerRecID] = {
                    files: item.NumberOfFiles,
                    rows: item.NumberOfJournalRows
                };
            });
        });

        // Get current customer id - wait for ViewScopeService's selectedCustomer$ (BehaviorSubject,
        // always replays its latest value) instead of reading it synchronously, to avoid racing
        // SelectAccountComponent, which populates the selected customer asynchronously on the same
        // tick this widget mounts.
        const currentCustomer = await firstValueFrom(this.viewScopeService.selectedCustomer$.pipe(
            filter((customer): customer is Customer => customer != null),
            take(1)
        ));
        this.currentCustomerId = currentCustomer.id;

        // Populate allActivities array and sort by pendingFiles descending
        this.allActivities = customers.map(c => ({
            id: c.id, // Add id for matching
            userName: c.name,
            pendingFiles: pendingMap[c.id]?.files || 0,
            pendingRows: pendingMap[c.id]?.rows || 0
        })).sort((a, b) => b.pendingFiles - a.pendingFiles);
        this.updateWorkSummary();
        this.filterActivities();
    }

    private updateWorkSummary(): void {
        this.currentCustomerActivity = this.allActivities.find(a => a.id === this.currentCustomerId) || null;
        this.totalPendingFiles = this.allActivities.reduce((total, activity) => total + activity.pendingFiles, 0);
        this.totalPendingRows = this.allActivities.reduce((total, activity) => total + activity.pendingRows, 0);
        this.currentPendingFiles = this.currentCustomerActivity?.pendingFiles || 0;
        this.currentPendingRows = this.currentCustomerActivity?.pendingRows || 0;
        this.otherPendingFiles = this.totalPendingFiles - this.currentPendingFiles;
        this.otherPendingRows = this.totalPendingRows - this.currentPendingRows;
        this.currentFilesShare = this.totalPendingFiles > 0 ? (this.currentPendingFiles / this.totalPendingFiles) * 100 : 0;
        this.currentRowsShare = this.totalPendingRows > 0 ? (this.currentPendingRows / this.totalPendingRows) * 100 : 0;
        this.setWorkComparisonChart();
    }

    private setWorkComparisonChart(): void {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue('--text-color').trim() || '#111827';
        const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary').trim() || '#64748b';
        const surfaceBorder = documentStyle.getPropertyValue('--surface-border').trim() || '#e5e7eb';
        const primaryColor = documentStyle.getPropertyValue('--p-primary-color').trim()
            || documentStyle.getPropertyValue('--primary-color').trim()
            || '#3b82f6';

        const primaryColor40 = primaryColor.startsWith('#')
            ? `${primaryColor}66`
            : primaryColor.replace('rgb', 'rgba').replace(')', ', 0.4)');

        this.workComparisonChartData = {
            labels: [this.translateServ.dictionary['PEN_FILES'], this.translateServ.dictionary['PEN_JORS']],
            datasets: [
                {
                    label: this.translateServ.dictionary['OTHER_CUSTOMERS'] || 'Other customers',
                    data: [this.otherPendingFiles, this.otherPendingRows],
                    backgroundColor: primaryColor40,
                    borderRadius: 0,
                    borderSkipped: false,
                    barThickness: 28
                },
                {
                    label: this.currentCustomerActivity?.userName || this.translateServ.dictionary['CUSTOMER'],
                    data: [this.currentPendingFiles, this.currentPendingRows],
                    backgroundColor: primaryColor,
                    borderRadius: 0,
                    borderSkipped: false,
                    barThickness: 28
                }
            ]
        };

        this.workComparisonChartOptions = {
            indexAxis: 'y',
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: {
                        color: textColor,
                        usePointStyle: true,
                        pointStyle: 'circle',
                        padding: 16
                    }
                },
                tooltip: {
                    callbacks: {
                        label: (context: any) => `${context.dataset.label}: ${context.formattedValue}`
                    }
                }
            },
            scales: {
                x: {
                    beginAtZero: true,
                    stacked: true,
                    ticks: {
                        color: textColorSecondary,
                        precision: 0
                    },
                    grid: {
                        color: surfaceBorder,
                        drawBorder: false
                    }
                },
                y: {
                    stacked: true,
                    ticks: {
                        color: textColor
                    },
                    grid: {
                        display: false,
                        drawBorder: false
                    }
                }
            }
        };
    }

    toggleShowPendingOnly(): void {
        this.showPendingOnly = !this.showPendingOnly;
        this.filterActivities();
    }

    filterActivities(): void {
        if (this.showPendingOnly) {
            this.customerActivities = this.allActivities.filter(a => a.pendingFiles > 0);
        } else {
            this.customerActivities = [...this.allActivities];
        }
    }

    isCurrentCustomer(activity: any): boolean {
        return activity.id === this.currentCustomerId;
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
}